<template>
  <div class="purchase-order-form">
    <form @submit.stop.prevent="form.handleSubmit()">
      <!-- Project, Purchase Type, PO# -->
      <div class="grid grid-cols-3 gap-4">
        <BaseInput
          :model-value="project.name"
          type="text"
          label="Project"
          :disabled="true"
        />

        <BaseSelect
          :model-value="purchaseOrder.purchaseType"
          label="Purchase Type"
          :options="purchaseTypeOptions"
          description="Select purchase type"
          :disabled="true"
        />

        <BaseInput
          :model-value="purchaseOrder.purchaseOrderId"
          type="text"
          label="PO#"
          :disabled="true"
        />
      </div>

      <div class="grid grid-cols-3 gap-4">
        <form.Field
          name="vendorId"
          :validators="{
            onBlur: yup.string().required('Vendor is required'),
          }"
        >
          <template #default="{ field }">
            <BaseSelect
              :model-value="(field.state.value as string)"
              :name="field.name"
              label="Vendor"
              :options="vendors"
              :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
              @blur="field.handleBlur"
              @input="(e) => field.handleChange(e.target.value)"
            />
          </template>
        </form.Field>

        <BaseInputDate
          :model-value="purchaseOrder.date"
          label="Purchase Order Date"
          :disabled="true"
        />

        <AddressAutoComplete
          :value="purchaseOrder.shippingAddress"
          label="Shipping Address"
          placeholder="Address"
          :disabled="true"
        />
      </div>

      <h4 class="mt-6">Items</h4>
      <hr class="mt-2">

      <div>
        <form.Field
          name="items"
          :validators="{
            onChange: yup.array().min(1, 'At least one item is required')
          }"
        >
          <template #default="{ field }">
            <div
              v-if="(field.state.meta.errors && field.state.meta.errors[0]) && form.getFieldValue('items').length === 0"
              class="text-base/6 text-error sm:text-sm/6 mt-2 flex flex-col items-center"
            >
              <span class="i-heroicons-exclaimation-circle size-8" />
              <p class="text-base/6 text-error sm:text-sm/6">
                {{ field.state.meta.errors[0] }}
              </p>
            </div>

            <div
              v-for="(_, i) of field.state.value"
              :key="i"
            >
              <div class="flex justify-end mt-8">
                <span
                  class="i-heroicons-x-circle cursor-pointer hover:text-error h-[1.2rem] w-[1.2rem]"
                  @click="removeItem(field, Number(i))"
                />
              </div>

              <!-- Title, Quantity, Price -->
              <div class="flex justify-around gap-6">
                <div class="grow">
                  <form.Field
                    :key="`name-${i}`"
                    :name="( `items[${i}].name` as any)"
                    :validators="{
                      onBlur: yup.string().required('Title is required'),
                    }"
                  >
                    <template #default="{ field: subField }">
                      <BaseInput
                        :model-value="(subField.state.value as string)"
                        type="text"
                        label="Title"
                        :error="(subField.state.meta.errors && subField.state.meta.errors[0] as string)"
                        @blur="subField.handleBlur"
                        @input="(e) => subField.handleChange(e.target.value)"
                      />
                    </template>
                  </form.Field>
                </div>

                <div class="mx-2">
                  <form.Field
                    :key="`quantity-${i}`"
                    :name="(`items[${i}].quantity` as any)"
                    :validators="{
                      onBlur: yup.string().transform((value) => value.replace(/,/g, '')).test('is-number','Quantity must be a positive number',(value) => !isNaN(Number(value)) && Number(value) > 0).required('Quantity is required')
                    }"
                  >
                    <template #default="{ field: subField }">
                      <BaseInput
                        v-model.number="(subField.state.value as string)"
                        type="number"
                        step="any"
                        label="Quantity"
                        :error="(subField.state.meta.errors && subField.state.meta.errors[0] as string)"
                        @blur="subField.handleBlur"
                        @input="(e) => {
                          subField.handleChange(e.target.value);
                          setTotal()
                        }"
                      />
                    </template>
                  </form.Field>
                </div>

                <div class="grow">
                  <form.Field
                    :key="`cost-${i}`"
                    :name="(`items[${i}].cost` as any)"
                    :validators="{
                      onBlur: yup.string().transform((value) => value.replace(/,/g, '')).test('is-number','Price must be a valid number',(value) => !isNaN(Number(value))).required('Price is required')
                    }"
                  >
                    <template #default="{ field: subField }">
                      <BaseInputCurrency
                        :model-value="(subField.state.value as string)"
                        label="Price"
                        :error="(subField.state.meta.errors && subField.state.meta.errors[0] as string)"
                        @blur="subField.handleBlur"
                        @input="(e) => {
                          subField.handleChange(e.target.value);
                          setTotal()
                        }"
                      />
                    </template>
                  </form.Field>
                </div>

                <div class="mx-2 min-w-24">
                  <label
                    class="font-semibold"
                    for="total-price"
                  >Total</label>
                  <span
                    id="total-price"
                    class="block mt-3"
                  >{{ currency(toNumber((field.state.value[i] as any).quantity) * toNumber((field.state.value[i] as any).cost)) }}</span>
                </div>
              </div>
            </div>

            <div class="my-5">
              <BaseButton
                variant="secondary"
                type="button"
                text="Add item"
                @click="addItem(field)"
              />
            </div>
          </template>
        </form.Field>
      </div>

      <div class="mt-5 w-fit ml-auto">
        <div class="flex items-baseline justify-end bg-slate-100 px-4 pt-4 rounded-lg gap-4">
          <span class="text-slate-700">Total</span>
          <form.Field
            name="total"
            :validators="{
              onBlur: yup.string().transform((value) => value.replace(/,/g, '')).test('is-number','Total must be a valid number',(value) => !isNaN(Number(value))).required('Total is required')
            }"
          >
            <template #default="{ field }">
              <BaseInputCurrency
                :model-value="(field.state.value as string)"
                :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
                @blur="field.handleBlur"
                @input="(e) => field.handleChange(e.target.value)"
              />
            </template>
          </form.Field>
        </div>
      </div>

      <hr class="mt-6">

      <div class="flex justify-end gap-2 mt-3">
        <form.Subscribe>
          <template #default="{ canSubmit, isSubmitting }">
            <BaseButton
              variant="base"
              text="Cancel"
              :to="{ name: 'ShowPurchaseOrder', params: { id: purchaseOrder.id } }"
              :disabled="isSubmitting"
              :class="{ 'cursor-not-allowed opacity-50': isSubmitting }"
            />

            <BaseButton
              variant="primary"
              text="Save"
              type="submit"
              :disabled="!canSubmit || isSubmitting"
              :class="{ 'cursor-not-allowed opacity-50': !canSubmit || isSubmitting }"
            />
          </template>
        </form.Subscribe>
      </div>
    </form>
  </div>
</template>
<script lang="ts" setup>
import { PurchaseOrder, PurchaseOrderItem, PO_PURCHASE_TYPE } from '@/models/purchaseOrder'
import BaseButton from '@/components/base/BaseButton.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import { map, extend, flatten, difference, intersection } from 'underscore'
import BaseSelect from '@/components/base/BaseSelect.vue'
import AddressAutoComplete from '@/components/base/AddressAutoComplete.vue'
import BaseInputCurrency from '@/components/base/BaseInputCurrency.vue'
import BaseInputDate from '@/components/base/BaseInputDate.vue'
import { currency, toNumber } from '@/utils'
import { PropType, ref } from 'vue'
import { Project } from '@/models/project'
import { useForm } from '@tanstack/vue-form'
import { yupValidator } from '@tanstack/yup-form-adapter'
import * as yup from 'yup'
import { decimalFormat } from '@/utils'

type selectOption = {
  label: string
  value: string,
  disabled?: boolean
  [key: string]: any
}

const props = defineProps({
  model: {
    type: Object as PropType<PurchaseOrder>,
    required: true
  },
  project: {
    type: Object as PropType<Project>,
    required: true
  },
  vendors: {
    type: Array as PropType<selectOption[]>,
    required: true
  },
  saveFunction: {
    type: Function,
    required: true
  }
})

const purchaseOrder = ref({ ...props.model })

const purchaseTypeOptions = [
  { value: PO_PURCHASE_TYPE.INVOICE, label: 'Purchase Order (vendor will invoice)' },
  { value: PO_PURCHASE_TYPE.EXPENSE, label: 'Expense (paid with cash or debit)' },
  { value: PO_PURCHASE_TYPE.REIMBURSEMENT, label: 'Reimbursement (company will reimburse me)' }
]

const form = useForm({
  defaultValues: purchaseOrder.value,
  validatorAdapter: yupValidator(),
  onSubmit: async ({ value }) => {
    const purchaseOrder = Object.fromEntries(Object.entries(value).filter(([, v]) => v !== undefined)) as unknown as PurchaseOrder

    purchaseOrder.items = purchaseOrder.items.map((item) => {
      const poItem = {
        ...item,
        cost: toNumber(item.cost),
        quantity: toNumber(item.quantity)
      }
      return Object.fromEntries(Object.entries(poItem).filter(([, v]) => v !== undefined)) as unknown as PurchaseOrderItem
    })
    purchaseOrder.total = toNumber(purchaseOrder.total)

    await props.saveFunction(purchaseOrder)
  },
})

const addItem = (field: any) => {
  field.pushValue({
    ...new PurchaseOrderItem(),
    quantity: 1,
    cost: 0
  })
}

const removeItem = (field: any, index: number) => {
  // save lineItemIds of the item to delete
  const poItem = { ...form.getFieldValue('items')[index] }
  let poItemLineItems = extend([], poItem.lineItems)

  // delete the item
  field.removeValue(index)

  // does the itemToDelete lineitemsIds match any of the other item lineitemsIds
  const match = map(form.getFieldValue('items'), (item: PurchaseOrderItem) => {
    return intersection(poItemLineItems, item.lineItems)
  })

  const noMatchFound = (!flatten(match)[0])
  if (noMatchFound) {
    // delete lineItemIds not matching any other
    form.setFieldValue('lineItems', difference(form.getFieldValue('lineItems'), poItemLineItems))
    poItemLineItems = []
  }
  setTotal()
}

const setTotal = () => {
  const total = form.getFieldValue('items').reduce((acc, item) => {
    return acc + (item.quantity * toNumber(item.cost))
  }, 0)

  if (total) {
    form.setFieldValue('total', decimalFormat(total, 2) as any)
  } else {
    form.setFieldValue('total', String(0) as any)
  }
}

</script>
